import {addDays, differenceInCalendarDays, subYears} from "date-fns";

// Period comparison for the "Résumé" tab: the selected range is compared
// against either the period of the same length right before it, or the same
// dates one year earlier (N-1).
export const COMPARE_MODES = ["none", "previousPeriod", "previousYear"];

/**
 * Build the comparison range for a [{startDate, endDate}] selection.
 * Returns null when comparison is off or the selection is incomplete.
 *
 * - previousPeriod: same number of days, ending the day before the start
 *   (1–7 Sept → 25–31 Aug).
 * - previousYear: same calendar dates one year earlier (1–7 Sept 2026 →
 *   1–7 Sept 2025).
 */
export const getComparisonRange = (dateRange, mode) => {
    const range = dateRange?.[0];
    if (!range?.startDate || !range?.endDate || !mode || mode === "none") return null;

    const start = new Date(range.startDate);
    const end = new Date(range.endDate);

    if (mode === "previousYear") {
        return [{startDate: subYears(start, 1), endDate: subYears(end, 1), key: "comparison"}];
    }

    if (mode === "previousPeriod") {
        const days = differenceInCalendarDays(end, start) + 1;
        return [{startDate: addDays(start, -days), endDate: addDays(start, -1), key: "comparison"}];
    }

    return null;
};

/**
 * Percentage change from `previous` to `current`.
 * Returns null when either value isn't numeric or `previous` is 0
 * (no meaningful percentage).
 */
export const computeDelta = (current, previous) => {
    if (current === null || current === undefined || current === "") return null;
    if (previous === null || previous === undefined || previous === "") return null;
    const cur = Number(current);
    const prev = Number(previous);
    if (!Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) return null;
    return ((cur - prev) / Math.abs(prev)) * 100;
};

// ── Chart helpers ───────────────────────────────────────────────────────────

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Parse a chart label as a local date. Supports "YYYY-MM-DD…" and
// "DD/MM/YYYY"; returns null for anything else (hours, names…).
const parseLabelDate = (label) => {
    const s = String(label ?? "");
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return {date: new Date(+m[1], m[2] - 1, +m[3]), style: "iso"};
    m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (m) return {date: new Date(+m[3], m[2] - 1, +m[1]), style: "fr"};
    return null;
};

const formatLabelDate = (date, style) => {
    const y = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return style === "fr" ? `${d}/${mo}/${y}` : `${y}-${mo}-${d}`;
};

const dayIndex = (date, start) => Math.round((startOfDayMs(date) - startOfDayMs(start)) / ONE_DAY_MS);
const startOfDayMs = (date) => {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/**
 * Line up a daily series with its comparison series, day N against day N of
 * the comparison period (e.g. 1 Sept ↔ 25 Aug for "previous period").
 *
 * Both series are [{label, value}]. Returns [{label, value, previous,
 * previousLabel}] — one row per day of the current range, 0 where a day has
 * no data. If the labels aren't dates (e.g. an hourly breakdown for a single
 * day), falls back to lining rows up by position.
 */
export const alignTimeSeries = (current, previous, currentRange, previousRange) => {
    const curStart = currentRange?.[0]?.startDate;
    const curEnd = currentRange?.[0]?.endDate;
    const prevStart = previousRange?.[0]?.startDate;

    const curParsed = current.map((p) => parseLabelDate(p.label));
    const prevParsed = previous.map((p) => parseLabelDate(p.label));
    const allDates = curStart && curEnd && prevStart
        && curParsed.every(Boolean) && prevParsed.every(Boolean)
        && (curParsed.length + prevParsed.length) > 0;

    if (!allDates) {
        const length = Math.max(current.length, previous.length);
        return Array.from({length}, (_, i) => ({
            label: current[i]?.label ?? previous[i]?.label ?? "",
            value: current[i]?.value ?? 0,
            previous: previous[i]?.value ?? 0,
            previousLabel: previous[i]?.label ?? "",
        }));
    }

    const style = (curParsed[0] || prevParsed[0]).style;
    const days = dayIndex(curEnd, curStart) + 1;
    const curByDay = new Map();
    const prevByDay = new Map();
    curParsed.forEach((p, i) => curByDay.set(dayIndex(p.date, curStart), current[i].value));
    prevParsed.forEach((p, i) => prevByDay.set(dayIndex(p.date, prevStart), previous[i].value));

    return Array.from({length: days}, (_, i) => ({
        label: formatLabelDate(new Date(startOfDayMs(curStart) + i * ONE_DAY_MS + ONE_DAY_MS / 2), style),
        value: curByDay.get(i) ?? 0,
        previous: prevByDay.get(i) ?? 0,
        previousLabel: formatLabelDate(new Date(startOfDayMs(prevStart) + i * ONE_DAY_MS + ONE_DAY_MS / 2), style),
    }));
};

/**
 * Merge a categorical series (hours, payment methods, services…) with its
 * comparison series by label. Labels only present in the comparison period
 * are kept (with 0 for the current period). Numeric-looking labels (hours)
 * are sorted numerically so the axis stays in order.
 */
export const mergeSeriesByLabel = (current, previous) => {
    const rows = new Map();
    current.forEach((p) => rows.set(p.label, {label: p.label, value: p.value, previous: 0}));
    previous.forEach((p) => {
        const row = rows.get(p.label) || {label: p.label, value: 0, previous: 0};
        row.previous = p.value;
        rows.set(p.label, row);
    });
    const list = [...rows.values()];
    const leadingNumber = (label) => parseFloat(String(label).match(/^-?\d+(\.\d+)?/)?.[0]);
    if (list.length && list.every((r) => Number.isFinite(leadingNumber(r.label)))) {
        list.sort((a, b) => leadingNumber(a.label) - leadingNumber(b.label));
    }
    return list;
};

// Styling shared by every comparison chart: the comparison series is grey
// and, on line/area charts, dashed.
export const COMPARISON_COLOR = "#9CA3AF";
export const comparisonLegend = (isDarkMode) => ({
    position: "bottom",
    textStyle: {color: isDarkMode ? "#D1D5DB" : "#374151", fontSize: 11},
});
export const dashedComparisonSeries = {1: {lineDashStyle: [4, 4], areaOpacity: 0.05}};
